import React, { ReactNode, useRef, useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useWidgetManager } from '@/contexts/WidgetManager';

interface WidgetProps {
  id: string;
  children: ReactNode;
  className?: string;
  position: { x: number; y: number };
  zIndex: number;
  showControls?: boolean;
}

export const Widget: React.FC<WidgetProps> = ({
  id,
  children,
  className,
  position,
  zIndex,
  showControls = true
}) => {
  const { closeWidget, focusWidget, moveWidget } = useWidgetManager();
  const widgetRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [initialPosition, setInitialPosition] = useState({ x: 0, y: 0 });

  const handleMouseDown = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    // Don't start dragging if clicking on control buttons, resize handles, or interactive elements
    if (
      target.closest('.widget-controls') ||
      target.closest('[class*="cursor-se-resize"]') ||
      target.closest('input, textarea, select, button, a, [contenteditable="true"], .widget-nodrag')
    ) {
      return;
    }

    e.preventDefault();
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
    setInitialPosition(position);
    focusWidget(id);
  };


  const handleClose = () => {
    closeWidget(id);
  };

  const handleWidgetClick = () => {
    focusWidget(id);
  };

  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;

      const deltaX = e.clientX - dragStart.x;
      const deltaY = e.clientY - dragStart.y;

      // Get viewport dimensions
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      
      // Define boundaries (top menu height is typically around 60px)
      const topBoundary = 60;
      const bottomBoundary = viewportHeight - 100; // Leave some space from bottom
      const leftBoundary = 0;
      const rightBoundary = viewportWidth - 200; // Leave some space from right

      const newPosition = {
        x: Math.max(leftBoundary, Math.min(rightBoundary, initialPosition.x + deltaX)),
        y: Math.max(topBoundary, Math.min(bottomBoundary, initialPosition.y + deltaY))
      };

      moveWidget(id, newPosition);
    };

    const handleGlobalMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isDragging, dragStart, initialPosition, id, moveWidget]);

  return (
    <div
      ref={widgetRef}
      className={cn(
        "fixed pointer-events-auto",
        isDragging 
          ? "cursor-grabbing transition-none select-none" 
          : "cursor-grab transition-all duration-200 ease-out select-none hover:shadow-xl",
        className
      )}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        zIndex: Math.min(zIndex, 40) // Ensure widgets stay below dock (z-index 50+)
      }}
      onMouseDown={handleMouseDown}
      onClick={handleWidgetClick}
    >
      {/* Widget Controls */}
      {showControls && (
        <div className="widget-controls absolute -top-3 -right-3 z-50 flex gap-1">
          <button
            onClick={handleClose}
            className="w-6 h-6 rounded-full bg-red-500/90 hover:bg-red-600 hover:scale-110 flex items-center justify-center transition-all duration-200 backdrop-blur-sm shadow-lg border border-white/20"
          >
            <X className="w-3.5 h-3.5 text-white" />
          </button>
        </div>
      )}

      {/* Widget Content - no wrapper styling */}
      <div className="relative">
        {children}
      </div>
    </div>
  );
};